/**
 * Gomme magique : the server half of shared.popups.MagicEraserPopup. Posts to the upload server
 * (MagicEraserController there, like the AI effects of BaseRepository) and polls until fal has
 * answered.
 *
 *   erase(photo, effect, mask) → Promise<edit>   the erase is done (edit.medium / edit.full)
 *   apply(edit, slot)          → Promise<{ edit, photo }>   kept : photo is the new photo
 *   discard(edit)              → Promise                  "Recommencer" : the result stays out
 *   keep(edit, component, slot, photos)      apply : a new photo holding the erased file, shown
 *                                   → Promise by the place erased in only (tag "magic-erase" :
 *                                             the stage keeps the framing)
 *
 * Both reject with an Error whose `code` is the server's refusal ("daily-limit", "throttled"…),
 * "failed" or "timeout" ; message(code) says it to the customer.
 */
fx.service(class MagicEraserService extends fx.Service
{
    constructor(model)
    {
        super();

        this.model = model.creationModel;

        this.POLL_DELAY = 1500;
        this.POLL_TIMEOUT = 120000; // Bria answers in 10-20 s, the queue may add some
    }

    message(code)
    {
        switch (code)
        {
            case "no-account": return fx.text("Veuillez vous reconnecter.");
            case "not-allowed": return fx.text("Cette photo n'est pas accessible.");
            case "disabled": return fx.text("La gomme magique est momentanément indisponible.");
            case "bad-version": return fx.text("Cette version de la photo ne peut pas être retouchée.");
            case "bad-mask": return fx.text("Le tracé n'a pas pu être lu, veuillez réessayer.");
            case "throttled": return fx.text("Veuillez patienter une minute avant le prochain effacement.");
            case "daily-limit": return fx.text("Vous avez atteint la limite d'effacements pour aujourd'hui.");
            case "timeout": return fx.text("Le traitement prend trop de temps, veuillez réessayer.");
            case "cancelled": return "";
            default: return fx.text("L'effacement a échoué, veuillez réessayer.");
        }
    }

    // the photo itself, once on the server : an erased photo is a photo of its own, and a version
    // of a photo (an AI look, a cut-out) would replace it with that look
    canErase(photo, effect)
    {
        return !!photo && !photo.isTemporary && !photo.processing && !effect;
    }

    isErased(effect)
    {
        return !!effect && effect.indexOf("erase-") === 0;
    }

    get api()
    {
        let server = this.model.servers.uploadServers.getItemAt(0);

        return server ? server.api : "";
    }

    /**
     * The erase as a Promise carrying cancel() : the customer may give up waiting. Cancelling
     * aborts the request on its way and stops the polling ; the job itself goes on on the server
     * (fal cannot be stopped, and is billed anyway), its result is simply never shown.
     */
    erase(photo, effect, mask)
    {
        let job = { cancelled : false, request : null, timer : null, reject : null };
        let creation = this.model.creation;

        let promise = new Promise((resolve, reject) => {
            job.reject = reject;

            this._post("magic-erase", { id : photo.id, creationid : creation ? creation.id : 0, effect : effect || "", mask : mask }, job)
                .then((result) => this._poll(result.edit, Date.now(), job))
                .then(resolve, reject);
        });

        promise.cancel = () => {
            if (job.cancelled)
                return;

            job.cancelled = true;
            clearTimeout(job.timer);

            if (job.request)
                this._ajax.abort(job.request);

            job.reject(this._error("cancelled"));
        };

        return promise;
    }

    // "Recommencer" : the result stays out of the album for good (its photo's link deleted)
    discard(edit)
    {
        return this._post("magic-erase/discard", { edit : edit.id });
    }

    // `slot` is the place of the album erased in : the server moves it too, once it is saved
    apply(edit, slot)
    {
        let component = slot && Utils.isID(slot.id) ? "" : (slot ? slot.id : "");

        return this._post("magic-erase/apply", { edit : edit.id, component : component });
    }

    /**
     * Keeping brings into the album the photo the erase made (a photo row of its own, named after
     * its file) : only `slot`, the place of the album the customer erased in, shows it (framing
     * kept), and `component` too when it is a copy of it being edited (mobile EditPhoto). The use
     * counts follow from the places (BaseRepository) ; the original, once no place of the album
     * shows it, leaves the album — `{ number : 0, deleted : 1 }`, which the save writes on its
     * creation_media link (CrudSaver), as the server did, so that a save queued before cannot bring
     * it back.
     */
    keep(edit, component, slot, photos)
    {
        slot = slot || component;

        return this.apply(edit, slot).then((result) => {
            let data = result.photo;
            let oldId = result.replaced;
            let former = photos.find({ id : oldId });

            // a Photo, not the plain object : the list would only make it a bare model, which has no
            // picture to load (load / getUrl), and the place would go on showing the original
            if (!photos.find({ id : data.id }))
                photos.addItem(new models.Photo(Object.assign(this._inherited(former), { number : 0, deleted : 0, effects : [] }, data)));

            for (let place of [slot, component])
                if (place && place.content == oldId)
                    place.update("magic-erase", { content : data.id, content_type : "" });

            let creation = this.model.creation;
            if (creation)
            {
                creation.updatePhotosData(data.id);

                if (former && !this._shown(creation, oldId))
                    former.update({ number : 0, deleted : 1 });
            }

            return result;
        });
    }

    /**
     * What the new photo takes from the one erased : what describes the photo, never what belongs to
     * its picture or its upload. A photo uploaded in this session keeps its local `blob`, which is
     * shown before any url — copied, the new photo showed the original picture ; `hash` / `oldid`
     * tie a photo to the places it was dropped in before its upload ended.
     */
    _inherited(former)
    {
        let inherited = {};

        if (former)
            for (let key of ["accountid", "creation_id", "latitude", "longitude", "mediatype", "validated", "docid", "guestid", "labels", "caption", "original_filename"])
                if (former[key] !== undefined)
                    inherited[key] = former[key];

        return inherited;
    }

    // a place of the creation shows the photo
    _shown(creation, id)
    {
        for (let i = 0; i < creation.elements.length; i++)
        {
            let components = creation.elements.getItemAt(i).components;

            for (let j = 0; j < components.length; j++)
            {
                let component = components.getItemAt(j);
                if (component && component.isPhoto && component.content == id)
                    return true;
            }
        }

        return false;
    }

    _poll(edit, started, job)
    {
        if (job && job.cancelled)
            return new Promise(() => {}); // erase() already rejected

        if (edit.status == "done")
            return edit;

        if (edit.status == "failed")
            return Promise.reject(this._error("failed"));

        if (Date.now() - started > this.POLL_TIMEOUT)
            return Promise.reject(this._error("timeout"));

        return new Promise((resolve) => { let timer = setTimeout(resolve, this.POLL_DELAY); if (job) job.timer = timer; })
                    .then(() => this._post("magic-erase/status", { edit : edit.id }, job))
                    .then((result) => this._poll(result.edit, started, job));
    }

    _post(action, data, job)
    {
        return new Promise((resolve, reject) => {
            if (job && job.cancelled)
                return;

            let request = this.post(this.api + action, data, (result) => {
                if (job && job.cancelled)
                    return;

                if (result && !result.error && result.success)
                    resolve(result);
                else
                    reject(this._error(result && result.details && result.details.error ? result.details.error : "failed"));
            });

            if (job)
                job.request = request;
        });
    }

    _error(code)
    {
        let error = new Error(code);
        error.code = code;

        return error;
    }
});
